import * as React from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Check, Search } from 'lucide-react';
import { AssignAgentIcon } from '@/components/inbox/icons/InboxActionIcons';
import { getAvatarColorFromId, getAssigneeInitial } from '@/utils/conversationListHelpers';
import { orderAgentsForAssignMenu } from '@/lib/crm/orderAssignAgents'
import { sanitizeAssigneeId } from '@/lib/crm/sanitizeAssigneeId';
import { cn } from '@/lib/utils';
import { useAgentsById, isInactiveAgent } from '@/hooks/useAssignableAgents';
import { InactivePill } from '@/components/reusable/InactivePill';

export interface AssignAgentOption {
  id: string;
  name?: string | null;
  email?: string | null;
  role?: string | null;
  helper?: string | null;
}

function agentSubtitle(a: AssignAgentOption): string | undefined {
  if (a.helper) return a.helper;
  const role = String(a.role ?? '').trim();
  if (role) return role.toUpperCase();
  return a.email || undefined;
}

export interface AssignAgentMenuProps {
  children: React.ReactNode;
  currentAgentId?: string | null;
  currentAgentName?: string | null;
  agents: AssignAgentOption[];
  onAssign: (agentId: string | null) => void;
  align?: 'start' | 'center' | 'end';
  disabled?: boolean;
  loading?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  width?: number;
  'data-testid'?: string;
  showUnassigned?: boolean;
  emptyText?: string;
}

const CARD_CLASS =
  'p-0 rounded-xl border border-[#edf1f7] shadow-lg bg-white overflow-hidden';

function AgentRow({
  initial,
  colors,
  label,
  helper,
  active,
  disabled,
  onClick,
  testId,
}: {
  initial: React.ReactNode;
  colors: { backgroundColor: string; color: string } | null;
  label: React.ReactNode;
  helper?: React.ReactNode;
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={cn(
        'w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors',
        disabled ? 'opacity-50 cursor-not-allowed' : active ? 'bg-[#F4F6FA]' : 'hover:bg-[#F4F6FA]',
      )}
    >
      <span
        className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold leading-none"
        style={
          colors
            ? { backgroundColor: colors.backgroundColor, color: colors.color }
            : undefined
        }
      >
        {initial}
      </span>
      <span className="flex-1 min-w-0 flex flex-col gap-0.5">
        <span
          className="truncate leading-[22px]"
          style={{ fontFamily: 'var(--wn-font)', fontSize: 14, fontWeight: 500, color: '#364153' }}
        >
          {label}
        </span>
        {helper && (
          <span
            className="truncate leading-[18px]"
            style={{ fontFamily: 'var(--wn-font)', fontSize: 12, color: '#6a7282' }}
          >
            {helper}
          </span>
        )}
      </span>
      {active && <Check className="w-3.5 h-3.5 text-[#1EB677] flex-shrink-0" strokeWidth={2.5} />}
    </button>
  );
}

export interface AssignAgentListProps {
  currentAgentId?: string | null;
  currentAgentName?: string | null;
  agents: AssignAgentOption[];
  onAssign: (agentId: string | null) => void;
  disabled?: boolean;
  loading?: boolean;
  autoFocus?: boolean;
  showUnassigned?: boolean;
  emptyText?: string;
}

export function AssignAgentList({
  currentAgentId,
  currentAgentName,
  agents,
  onAssign,
  disabled = false,
  loading = false,
  autoFocus = true,
  showUnassigned = true,
  emptyText,
}: AssignAgentListProps) {
  const [query, setQuery] = React.useState('');

  const effectiveAgentId = React.useMemo(
    () => sanitizeAssigneeId(currentAgentId),
    [currentAgentId],
  );

  const directory = useAgentsById();

  const orphanedAssignee = React.useMemo<AssignAgentOption | null>(() => {
    if (!effectiveAgentId) return null;
    if (agents.some((a) => a.id === effectiveAgentId)) return null;
    const name = currentAgentName?.trim() || directory[effectiveAgentId]?.name?.trim() || null;
    if (!loading && !name) return null;
    return { id: effectiveAgentId, name, email: null };
  }, [agents, effectiveAgentId, currentAgentName, loading, directory]);

  const orphanIsInactive = !!orphanedAssignee && isInactiveAgent(directory[orphanedAssignee.id]);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const matched = !q
      ? agents
      : agents.filter((a) => {
          const n = (a.name || '').toLowerCase();
          const e = (a.email || '').toLowerCase();
          const r = (a.role || '').toLowerCase();
          const h = (a.helper || '').toLowerCase();
          return n.includes(q) || e.includes(q) || r.includes(q) || h.includes(q);
        });
    return orderAgentsForAssignMenu(matched, effectiveAgentId);
  }, [agents, query, effectiveAgentId]);

  const choose = (id: string | null) => {
    if (disabled) return;
    onAssign(id);
  };

  return (
    <>
      <div className="px-3 py-2.5 border-b border-[#edf1f7]">
        <div className="flex items-center gap-2 h-8 px-2.5 rounded-lg bg-[#fbfcfd] border border-[#edf1f7]">
          <Search className="w-3.5 h-3.5 text-[#99a1af] flex-shrink-0" />
          <input
            autoFocus={autoFocus}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search agents…"
            className="flex-1 min-w-0 bg-transparent border-none outline-none text-[14px] text-[#101828] placeholder:text-[#99a1af]"
            style={{ fontFamily: 'var(--wn-font)' }}
            data-testid="assign-agent-search"
          />
        </div>
      </div>

      <div className="max-h-[280px] overflow-y-auto scrollbar-hide py-1.5">
        {showUnassigned && (
          <AgentRow
            initial={<AssignAgentIcon className="w-3.5 h-3.5 text-[#6a7282]" />}
            colors={{ backgroundColor: '#F4F6FA', color: '#6a7282' }}
            label="Unassigned"
            active={!effectiveAgentId}
            disabled={disabled}
            onClick={() => choose(null)}
            testId="assign-agent-option-unassigned"
          />
        )}

        {orphanedAssignee && (
          <AgentRow
            initial={getAssigneeInitial(orphanedAssignee.name || null, orphanedAssignee.id)}
            colors={getAvatarColorFromId(orphanedAssignee.id)}
            label={
              orphanIsInactive ? (
                <span className="inline-flex max-w-full items-center gap-1.5">
                  <span className="truncate">{orphanedAssignee.name || 'Currently assigned'}</span>
                  <InactivePill />
                </span>
              ) : orphanedAssignee.name || 'Currently assigned'
            }
            helper={orphanIsInactive ? 'Pick an active member to reassign' : orphanedAssignee.name ? undefined : loading ? 'Loading…' : undefined}
            active
            disabled={disabled || orphanIsInactive}
            onClick={() => choose(orphanedAssignee.id)}
            testId="assign-agent-orphan-current"
          />
        )}

        {loading ? (
          <p
            className="px-3 py-4 text-center"
            style={{ fontFamily: 'var(--wn-font)', fontSize: 14, color: '#6a7282' }}
          >
            Loading agents…
          </p>
        ) : filtered.length === 0 ? (
          <p
            className="px-3 py-4 text-center"
            style={{ fontFamily: 'var(--wn-font)', fontSize: 14, color: '#6a7282' }}
          >
            {query ? 'No agents match' : emptyText || 'No agents available'}
          </p>
        ) : (
          filtered.map((a) => (
            <AgentRow
              key={a.id}
              initial={getAssigneeInitial(a.name || null, a.id)}
              colors={getAvatarColorFromId(a.id)}
              label={a.name || a.email || a.id}
              helper={agentSubtitle(a)}
              active={a.id === effectiveAgentId}
              disabled={disabled}
              onClick={() => choose(a.id)}
              testId={`assign-agent-option-${a.id}`}
            />
          ))
        )}
      </div>
    </>
  );
}

export function AssignAgentMenu({
  children,
  currentAgentId,
  currentAgentName,
  agents,
  onAssign,
  align = 'end',
  disabled = false,
  loading = false,
  open: openProp,
  onOpenChange,
  width = 264,
  'data-testid': dataTestId,
  showUnassigned = true,
  emptyText,
}: AssignAgentMenuProps) {
  const isControlled = openProp !== undefined;
  const [openState, setOpenState] = React.useState(false);
  const open = isControlled ? openProp! : openState;

  const setOpen = (next: boolean) => {
    if (!isControlled) setOpenState(next);
    onOpenChange?.(next);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
        <PopoverContent
        align={align}
        sideOffset={8}
        className={cn(CARD_CLASS, 'z-[80]')}
        style={{ width }}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        data-testid={dataTestId}
        data-no-dnd="true"
      >
        <AssignAgentList
          currentAgentId={currentAgentId}
          currentAgentName={currentAgentName}
          agents={agents}
          disabled={disabled}
          loading={loading}
          showUnassigned={showUnassigned}
          emptyText={emptyText}
          onAssign={(id) => {
            setOpen(false);
            onAssign(id);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

export default AssignAgentMenu;
